import { HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { SessionService } from '../services/session.service';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { isBlacklistedSessionUrl } from '../../utils/session-url-blacklist.util';
import { SESSION_URL_BLACKLIST } from '../../tokens/session-url-blacklist.token';

/**
 * Helper method to determine if the request should be excluded
 * @param req The request to check if should be excluded from the interceptor logic.
 * @returns A boolean indicating if the request should be excluded.
 */
const shouldExclude = (req: HttpRequest<unknown>): boolean => {
  // Define URLs or paths that don't require the token
  const excludedPaths = [
    '/login',
    '/register',
    '/public-api',
    '/confirm-code',
    '/reset-password',
    '/waitlist',
    'cloudfunctions.net',
    'unsplash.com',
    'facebook.com',
  ];

  // You can also exclude based on HTTP methods, e.g., req.method === 'GET'
  return excludedPaths.some((path) => req.url.includes(path));
};

export const authenticationInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  // Exclude /assets/ requests from API base URL prepending
  if (req.url.startsWith('/assets/')) {
    return next(req);
  }

  // Avoid double slashes when concatenating
  const apiUrl = environment.api.replace(/\/$/, '');
  const reqUrl = req.url.replace(/^\//, '');

  if (req.url.indexOf('anothertrip.com/o/') !== -1) {
    req = req.clone({
      headers: req.headers.set('Content-Type', 'application/json').set('Client-Id', environment.operatorClientId),
    });
    return next(req);
  }

  req =
    req.url.indexOf('cloudfunctions.net') !== -1 ||
    req.url.indexOf('unsplash.com') !== -1 ||
    req.url.indexOf('facebook.com') !== -1
      ? req.clone({
          headers: req.headers.set('Content-Type', 'application/json'),
        })
      : req.clone({
          url: `${apiUrl}/${reqUrl}`,
          headers: req.headers.set('Client-Id', environment.clientId).set('Content-Type', 'application/json'),
        });

  if (shouldExclude(req)) {
    // If it should be excluded, forward the request without modification
    return next(req);
  }

  const sessionService = inject(SessionService);
  const session = sessionService.sessionData;

  // Get and store current URL to restore after login
  const router = inject(Router);
  const blacklist = inject(SESSION_URL_BLACKLIST);

  const isBlacklisted: boolean = isBlacklistedSessionUrl(router.url, blacklist);

  if (isBlacklisted) {
    sessionService.clearSessionUrl();
  } else {
    sessionService.setSessionUrl(router.url);
  }

  if (session?.accessToken) {
    req = req.clone({
      headers: req.headers.set('Authorization', `${session.accessToken}`).set('id-token', session.idToken),
    });
  }

  return next(req);
};
